import { useEffect, useRef } from 'react';

interface VideoPlaybackProps {
  src: string | null;
}

// MediaRecorder WebM blobs omit the duration header, so the browser can't
// determine total length and the seek bar is wrong. The fix: after metadata
// loads, if duration is not finite, seek to a huge time — the browser scans
// the file to find the actual end, sets the real duration, then we reset to 0.
const VideoPlayback = ({ src }: VideoPlaybackProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;

    let fixed = false;

    const tryFix = () => {
      if (fixed || isFinite(v.duration)) return;
      v.currentTime = 1e101; // seek past end → browser resolves real duration
    };

    const onTimeUpdate = () => {
      if (!fixed && isFinite(v.duration)) {
        fixed = true;
        v.currentTime = 0;
      }
    };

    v.addEventListener('loadedmetadata', tryFix);
    v.addEventListener('timeupdate', onTimeUpdate);
    tryFix(); // in case metadata already loaded

    return () => {
      v.removeEventListener('loadedmetadata', tryFix);
      v.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      src={src || undefined}
      controls
      playsInline
      preload="metadata"
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  );
};

export default VideoPlayback;
